import * as XLSX from 'xlsx';
import type { Transaction } from '@/types/Transaction';
import type { CanonicalIndicators, IndicatorValue } from '@/types/Aggregates';
import { matchesAtCent, roundToCents } from './money';

/**
 * Valores esperados extraídos directamente do .xlsx, célula a célula.
 * Substitui o snapshot hardcoded do PROMPT_DASHBOARD §2.4 — assim o dashboard
 * acompanha automaticamente alterações futuras de Mensalidades sem ficar stale.
 */
export interface ExpectedTotals {
  /** Mens!D167 — soma da coluna D Mensalidades. */
  totalGross: number;
  /** Mens!E167. */
  totalNet: number;
  /** Mens!F167. */
  totalRefund: number;
  /** Fee!D16 — fee anual s/IVA. */
  feeAnnualNet: number;
  /** Fee!E16 — fee anual só IVA. */
  feeAnnualIva: number;
  /** Fee!F16 — fee anual c/IVA. */
  feeAnnualGross: number;
  /** Conta!D23 — Total Global Contrato c/IVA. */
  totalContractGross: number;
  /** COUNTIF(Inventario!E:E, "Ativa"). null se sheet/coluna não encontrada. */
  activePlatforms: number | null;
}

const CONTRACT_MONTHS = 12;

export function readExpectedTotals(workbook: XLSX.WorkBook): ExpectedTotals {
  const mens = workbook.Sheets['Mensalidades'];
  const fee = workbook.Sheets['Fee'];
  const conta = workbook.Sheets['Conta'];
  const inventario = workbook.Sheets['Inventario'];

  if (!mens) throw new Error('Sheet "Mensalidades" não encontrada.');
  if (!fee) throw new Error('Sheet "Fee" não encontrada.');
  if (!conta) throw new Error('Sheet "Conta" não encontrada.');

  return {
    totalGross: readNumericCell(mens, 'D167'),
    totalNet: readNumericCell(mens, 'E167'),
    totalRefund: readNumericCell(mens, 'F167'),
    feeAnnualNet: readNumericCell(fee, 'D16'),
    feeAnnualIva: readNumericCell(fee, 'E16'),
    feeAnnualGross: readNumericCell(fee, 'F16'),
    totalContractGross: readNumericCell(conta, 'D23'),
    activePlatforms: inventario ? countActivePlatforms(inventario) : null,
  };
}

function readNumericCell(sheet: XLSX.WorkSheet, addr: string): number {
  const cell = sheet[addr] as XLSX.CellObject | undefined;
  if (!cell) return 0;
  if (typeof cell.v === 'number') return cell.v;
  if (typeof cell.v === 'string') {
    const cleaned = cell.v
      .replace(/€/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function countActivePlatforms(inventario: XLSX.WorkSheet): number {
  const rows = XLSX.utils.sheet_to_json<unknown[]>(inventario, {
    header: 1,
    raw: true,
    defval: null,
  });
  // Coluna E (index 4) = Estado conforme PROMPT_DASHBOARD §6 Aba 5
  let count = 0;
  for (const row of rows) {
    const state = row?.[4];
    if (typeof state === 'string' && state.trim().toLowerCase() === 'ativa') {
      count++;
    }
  }
  return count;
}

/**
 * Calcula os 14 indicadores canónicos a partir das Transaction[] e dos totais
 * esperados lidos do .xlsx. Cada indicador devolve { expected, actual, diff, match }
 * para que o Compliance possa mostrar a tabela completa.
 */
export function computeCanonicalIndicators(
  transactions: Transaction[],
  expected: ExpectedTotals,
): CanonicalIndicators {
  const totalGrossActual = sumBy(transactions, (t) => t.gross);
  const totalNetActual = sumBy(transactions, (t) => t.net);
  const totalRefundActual = sumBy(transactions, (t) => t.refund);
  const totalContractActual = totalRefundActual + expected.feeAnnualGross;

  const uniqueSuppliers = new Set(transactions.map((t) => t.supplier)).size;
  const uniqueCategories = new Set(transactions.map((t) => t.category).filter(Boolean)).size;

  const avgMonthlyGross = totalGrossActual / CONTRACT_MONTHS;
  const avgMonthlyRefund = totalRefundActual / CONTRACT_MONTHS;
  const avgPerPlatformMonth =
    expected.activePlatforms && expected.activePlatforms > 0
      ? avgMonthlyGross / expected.activePlatforms
      : 0;

  return {
    totalGross: indicator(totalGrossActual, expected.totalGross),
    totalNet: indicator(totalNetActual, expected.totalNet),
    totalRefund: indicator(totalRefundActual, expected.totalRefund),
    feeAnnualNet: indicator(expected.feeAnnualNet, expected.feeAnnualNet),
    feeAnnualIva: indicator(expected.feeAnnualIva, expected.feeAnnualIva),
    feeAnnualGross: indicator(expected.feeAnnualGross, expected.feeAnnualGross),
    totalContractGross: indicator(totalContractActual, expected.totalContractGross),
    avgMonthlyGross: indicator(avgMonthlyGross, expected.totalGross / CONTRACT_MONTHS),
    avgMonthlyRefund: indicator(avgMonthlyRefund, expected.totalRefund / CONTRACT_MONTHS),
    activePlatforms: indicator(expected.activePlatforms ?? 0, expected.activePlatforms),
    avgPerPlatformMonth: indicator(avgPerPlatformMonth, null),
    uniqueSuppliers: indicator(uniqueSuppliers, null),
    uniqueCategories: indicator(uniqueCategories, null),
    transactionCount: indicator(transactions.length, null),
  };
}

function indicator(actual: number, expected: number | null): IndicatorValue {
  if (expected == null) {
    return { expected: null, actual: roundToCents(actual), diff: null, match: undefined };
  }
  const diff = roundToCents(actual - expected);
  return {
    expected: roundToCents(expected),
    actual: roundToCents(actual),
    diff,
    match: matchesAtCent(actual, expected),
  };
}

function sumBy<T>(items: T[], selector: (t: T) => number): number {
  let s = 0;
  for (const t of items) s += selector(t);
  return s;
}

/** Tabela amiga de devtools (console.table). */
export interface IndicatorRow {
  Indicador: string;
  Esperado: string;
  Calculado: string;
  'Diff (€)': string;
  Estado: '✓' | '✗' | '—';
}

const INDICATOR_LABELS: Record<keyof CanonicalIndicators, string> = {
  totalGross: 'Σ Bruto (Mens!D167)',
  totalNet: 'Σ Líquido (Mens!E167)',
  totalRefund: 'Σ Reembolso APM (Mens!F167)',
  feeAnnualNet: 'Fee anual s/IVA (Fee!D16)',
  feeAnnualIva: 'Fee anual IVA (Fee!E16)',
  feeAnnualGross: 'Fee anual c/IVA (Fee!F16)',
  totalContractGross: 'Total Contrato c/IVA (Conta!D23)',
  avgMonthlyGross: 'Custo mensal médio bruto',
  avgMonthlyRefund: 'Custo mensal médio reembolso',
  activePlatforms: 'Plataformas activas',
  avgPerPlatformMonth: 'Custo médio /plataforma/mês',
  uniqueSuppliers: 'Fornecedores únicos',
  uniqueCategories: 'Categorias únicas',
  transactionCount: 'Nº transacções',
};

export function indicatorsToDevtoolsTable(indicators: CanonicalIndicators): IndicatorRow[] {
  const rows: IndicatorRow[] = [];
  for (const [key, label] of Object.entries(INDICATOR_LABELS) as [
    keyof CanonicalIndicators,
    string,
  ][]) {
    const ind = indicators[key];
    rows.push({
      Indicador: label,
      Esperado: ind.expected == null ? '—' : formatNumericForDevtools(ind.expected, key),
      Calculado: formatNumericForDevtools(ind.actual, key),
      'Diff (€)': ind.diff == null ? '—' : ind.diff.toFixed(2),
      Estado: ind.match === undefined ? '—' : ind.match ? '✓' : '✗',
    });
  }
  return rows;
}

function formatNumericForDevtools(value: number, key: keyof CanonicalIndicators): string {
  // Contagens (inteiros) sem casas decimais
  if (
    key === 'activePlatforms' ||
    key === 'uniqueSuppliers' ||
    key === 'uniqueCategories' ||
    key === 'transactionCount'
  ) {
    return String(Math.round(value));
  }
  return value.toFixed(2);
}
